import React, {useCallback, useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser} from "../../utils/utility";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus, faTag, faTimes} from "@fortawesome/free-solid-svg-icons";

/**
 * Label component for assets.
 * - Shows existing labels as compact pills
 * - Tag button opens a popup for adding/removing labels
 */
export default function LabelInput({ assetId, labels = [], onLabelsChange }) {
  const [inputValue, setInputValue] = useState("");
  const [allLabels, setAllLabels] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    fetchAllLabels();
  }, []);

  // Position the portal popup below the tag button
  const updatePopupPosition = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPopupPos({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, []);

  // Close popup on outside click or scroll
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setShowPopup(false);
      }
    };
    const handleScroll = () => {
      if (showPopup) updatePopupPosition();
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showPopup, updatePopupPosition]);

  const fetchAllLabels = async () => {
    try {
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.ASSET_LABELS}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const resp = await axios.get(url);
      if (resp.status === 200) {
        setAllLabels(resp.data.responseData || []);
      }
    } catch {
      // silently fail
    }
  };

  const addLabel = (labelName) => {
    const currentLabelNames = labels.map((l) => l.labelName.toLowerCase());
    if (currentLabelNames.includes(labelName.toLowerCase())) return;

    const newLabels = [...labels.map((l) => l.labelName), labelName];
    saveLabels(newLabels);
    setInputValue("");
  };

  const removeLabel = (labelName) => {
    const newLabels = labels
      .filter((l) => l.labelName !== labelName)
      .map((l) => l.labelName);
    saveLabels(newLabels);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      addLabel(inputValue.trim());
    }
    if (e.key === "Escape") {
      setShowPopup(false);
    }
  };

  const saveLabels = async (labelNames) => {
    try {
      const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.ASSET_LABELS_ASSIGN}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const resp = await axios.post(url, {
        assetId: assetId,
        labelNames: labelNames,
      });
      if (resp.status === 200) {
        const savedLabels = resp.data.responseData || [];
        onLabelsChange(assetId, savedLabels);
        fetchAllLabels();
      }
    } catch {
      // silently fail
    }
  };

  const currentLabelNames = labels.map((l) => l.labelName.toLowerCase());

  // Labels available to add (not already assigned)
  const availableLabels = allLabels.filter(
    (l) => !currentLabelNames.includes(l.labelName.toLowerCase())
  );

  // Filtered by search
  const filteredLabels =
    inputValue.trim().length > 0
      ? availableLabels.filter((l) =>
          l.labelName.toLowerCase().includes(inputValue.toLowerCase())
        )
      : availableLabels;

  const canCreateNew =
    inputValue.trim().length > 0 &&
    !allLabels.some(
      (l) => l.labelName.toLowerCase() === inputValue.trim().toLowerCase()
    ) &&
    !currentLabelNames.includes(inputValue.trim().toLowerCase());

  return (
    <div className="label-input-wrapper">
      {/* Display existing labels as pills */}
      <div className="label-pills-container">
        {labels.map((label) => (
          <span key={label.labelId || label.labelName} className="label-pill">
            {label.labelName}
            <button
              className="label-pill-remove"
              onClick={(e) => {
                e.stopPropagation();
                removeLabel(label.labelName);
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </span>
        ))}

        {/* Tag button to open popup */}
        <button
          ref={btnRef}
          className="label-add-btn"
          title="Manage labels"
          onClick={(e) => {
            e.stopPropagation();
            if (!showPopup) updatePopupPosition();
            setShowPopup(!showPopup);
            setInputValue("");
          }}
        >
          <FontAwesomeIcon icon={faTag} />
          <span className="label-add-btn-text">+</span>
        </button>
      </div>

      {/* Popup rendered via portal to escape table overflow clipping */}
      {showPopup && ReactDOM.createPortal(
        <div
          className="label-popup"
          ref={popupRef}
          style={{ top: popupPos.top, left: popupPos.left }}
        >
          <div className="label-popup-header">
            <span className="label-popup-title">Manage Labels</span>
            <button
              className="label-popup-close"
              onClick={() => setShowPopup(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="label-popup-search">
            <input
              type="text"
              className="label-popup-input"
              placeholder="Search or create label..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </div>

          {/* Currently assigned labels */}
          {labels.length > 0 && (
            <div className="label-popup-section">
              <div className="label-popup-section-title">Assigned</div>
              {labels.map((label) => (
                <div
                  key={label.labelId || label.labelName}
                  className="label-popup-item assigned"
                  onClick={() => removeLabel(label.labelName)}
                >
                  <span className="label-popup-item-name">
                    {label.labelName}
                  </span>
                  <FontAwesomeIcon
                    icon={faTimes}
                    className="label-popup-item-action"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Available labels to add */}
          {filteredLabels.length > 0 && (
            <div className="label-popup-section">
              <div className="label-popup-section-title">Available</div>
              {filteredLabels.map((label) => (
                <div
                  key={label.labelId}
                  className="label-popup-item"
                  onClick={() => addLabel(label.labelName)}
                >
                  <span className="label-popup-item-name">
                    {label.labelName}
                  </span>
                  <FontAwesomeIcon
                    icon={faPlus}
                    className="label-popup-item-action add"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Create new label option */}
          {canCreateNew && (
            <div className="label-popup-section">
              <div
                className="label-popup-item create-new"
                onClick={() => addLabel(inputValue.trim())}
              >
                <FontAwesomeIcon icon={faPlus} />
                <span className="label-popup-item-name">
                  Create "{inputValue.trim()}"
                </span>
              </div>
            </div>
          )}

          {filteredLabels.length === 0 &&
            !canCreateNew &&
            labels.length === 0 && (
              <div className="label-popup-empty">
                No labels yet. Type to create one.
              </div>
            )}
        </div>,
        document.body
      )}
    </div>
  );
}
